// payoutmanagement/usePayoutManagement.js — 정산 관리 상태·이펙트·핸들러 훅
// companyCode prop에 따라 해당 회사의 정산 데이터를 로드
// 모달(계좌·세금정보) 핸들러는 usePayoutModalHandlers.js에 분리

import { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { usePayoutModalHandlers } from './usePayoutModalHandlers';
import { todayKST, parseYmd } from '../../../utils/date';

// ── 이번 달 YYYY-MM (KST 기준) ────────────────────────────────────────────────
function thisMonthKST() {
  return todayKST().slice(0, 7);
}

export function usePayoutManagement({ companyCode = 'PINE' }) {
  const { showToast } = useToast();

  // 모바일 감지
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── 달력 상태 ──────────────────────────────────────────────────────────────
  const [month, setMonth]           = useState(thisMonthKST); // 'YYYY-MM'
  const [calendarData, setCalendar] = useState([]);            // [{ date, person_count, total_amount }]
  const [calLoading, setCalLoading] = useState(false);

  // ── 목록 상태 ──────────────────────────────────────────────────────────────
  const [listData, setListData]       = useState([]);
  const [listLoading, setListLoading] = useState(false);
  // 과세유형 기능 스위치 (settlement_settings.payout_tax_type_enabled, 회사별) — OFF면 UI 무변화
  const [taxTypeEnabled, setTaxTypeEnabled] = useState(false);

  // ── 은행코드 (활성만, 드롭다운용) ─────────────────────────────────────────
  const [bankCodes, setBankCodes] = useState([]);

  // ── 선택된 날짜 (달력 클릭 → 해당 날짜 상세 목록 표시) ────────────────────
  const [selectedDate, setSelectedDate] = useState(null);

  // ══════════════════════════════════════════════════════════════════════════
  // ── 데이터 로드 ──────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  // 은행코드 (활성만)
  const fetchBankCodes = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/admin/payout/bank-codes');
      if (res.data?.success) setBankCodes(res.data.data || []);
    } catch (_) {}
  }, []);

  // 달력 요약
  const fetchCalendar = useCallback(async () => {
    setCalLoading(true);
    try {
      const res = await apiClient.get(`/api/admin/payout/calendar?companyCode=${companyCode}&month=${month}`);
      if (res.data?.success) setCalendar(res.data.data || []);
    } catch (_) {
      showToast('달력 데이터 로드 실패', 'error');
    } finally {
      setCalLoading(false);
    }
  }, [companyCode, month, showToast]);

  // 목록
  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await apiClient.get(`/api/admin/payout/list?companyCode=${companyCode}&month=${month}`);
      if (res.data?.success) {
        setListData(res.data.data || []);
        setTaxTypeEnabled(res.data.taxTypeEnabled === true);
      }
    } catch (_) {
      showToast('목록 데이터 로드 실패', 'error');
    } finally {
      setListLoading(false);
    }
  }, [companyCode, month, showToast]);

  // 초기 로드 + month 변경 시 갱신
  useEffect(() => { fetchBankCodes(); }, [fetchBankCodes]);
  useEffect(() => { fetchCalendar(); fetchList(); }, [fetchCalendar, fetchList]);

  // ── 모달 핸들러 위임 ──────────────────────────────────────────────────────
  // 계좌/세금정보 편집 모달 상태·핸들러는 별도 훅으로 분리
  // companyCode를 모달 핸들러에 전달 — 계좌·세금정보가 회사별로 독립 관리됨
  const modalHandlers = usePayoutModalHandlers({ showToast, fetchBankCodes, fetchList, companyCode });

  // ══════════════════════════════════════════════════════════════════════════
  // ── 엑셀 다운로드 ────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  const downloadExcel = useCallback(async (date) => {
    try {
      const res = await apiClient.get(`/api/admin/payout/excel?companyCode=${companyCode}&date=${date}`);
      if (!res.data?.success) {
        showToast(res.data?.message || '엑셀 데이터 조회 실패', 'error');
        return;
      }
      const { fileName, rows, missing } = res.data;

      // 미등록 계좌 경고
      if (missing && missing.length > 0) {
        const names = missing.map((m) => m.name).join(', ');
        showToast(`계좌 미등록 ${missing.length}명 제외: ${names}`, 'error');
      }
      if (rows.length === 0) {
        showToast('다운로드할 데이터가 없습니다. (계좌 미등록)', 'error');
        return;
      }

      // XLSX 생성 (헤더 없이 데이터만)
      const wsData = rows.map((r) => [r.bankCode, r.accountNumber, Number(r.amount), r.accountHolder, r.senderName]);
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [{ wch: 10 }, { wch: 20 }, { wch: 14 }, { wch: 12 }, { wch: 12 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '급여이체');
      XLSX.writeFile(wb, `${fileName}.xlsx`);
      showToast(`${fileName}.xlsx 다운로드 완료`, 'success');
    } catch (_) {
      showToast('엑셀 다운로드 실패', 'error');
    }
  }, [companyCode, showToast]);

  // ── 선지급분 엑셀 다운로드 (현재 선택 월 전체) ───────────────────────────
  const downloadPrepayExcel = useCallback(async () => {
    try {
      const res = await apiClient.get(`/api/admin/payout/excel-prepay?companyCode=${companyCode}&month=${month}`);
      if (!res.data?.success) {
        showToast(res.data?.message || '데이터 조회 실패', 'error');
        return;
      }
      const { fileName, rows } = res.data;
      if (!rows.length) { showToast('해당 월 지급 내역이 없습니다.', 'error'); return; }

      // 콤마 숫자 포맷 적용 헬퍼 (지정 컬럼들)
      const applyComma = (ws, cols) => {
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let rowIdx = 1; rowIdx <= range.e.r; rowIdx++) {
          for (const c of cols) {
            const cell = ws[XLSX.utils.encode_cell({ r: rowIdx, c })];
            if (cell) cell.z = '#,##0';
          }
        }
      };
      const buildIndividualSheet = (list) => {
        const header = ['정산일', '성명', '주민번호', '급여구분', '지급합계'];
        const ws = XLSX.utils.aoa_to_sheet([header, ...list.map((r) => [r.정산일, r.성명, r.주민번호, r.급여구분, r.지급합계])]);
        applyComma(ws, [4]);
        ws['!cols'] = [{ wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 8 }, { wch: 12 }];
        return ws;
      };

      const wb = XLSX.utils.book_new();
      const businessRows = res.data.taxTypeEnabled ? rows.filter((r) => r.tax_type === 'business') : [];
      if (businessRows.length === 0) {
        // 사업자 없음 — 기존과 완전히 동일한 단일 시트
        XLSX.utils.book_append_sheet(wb, buildIndividualSheet(rows), '선지급분');
      } else {
        // 신고 명단 오염 방지 — 개인 3.3% 와 사업자 부가세를 시트로 분리
        const individualRows = rows.filter((r) => r.tax_type !== 'business');
        if (individualRows.length > 0) {
          XLSX.utils.book_append_sheet(wb, buildIndividualSheet(individualRows), '개인 3.3%');
        }
        const bizHeader = ['정산일', '상호', '사업자번호', '성명', '공급가액', '부가세', '합계'];
        const bizWs = XLSX.utils.aoa_to_sheet([
          bizHeader,
          ...businessRows.map((r) => [r.정산일, r.business_name || '', r.business_number || '', r.성명, r.supply_amount, r.vat_amount, r.total_with_vat]),
        ]);
        applyComma(bizWs, [4, 5, 6]);
        bizWs['!cols'] = [{ wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, bizWs, '사업자 부가세');
      }
      XLSX.writeFile(wb, `${fileName}.xlsx`);
      showToast(`${fileName}.xlsx 다운로드 완료`, 'success');
    } catch (_) {
      showToast('엑셀 다운로드 실패', 'error');
    }
  }, [companyCode, month, showToast]);

  // ══════════════════════════════════════════════════════════════════════════
  // ── 월 이동 ──────────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  // 월 이동: 문자열 연산으로 처리 (new Date 직접 사용 금지 — utils/date.js 사용)
  const prevMonth = useCallback(() => {
    setMonth((m) => {
      const { year, month } = parseYmd(m + '-01');
      const py = month === 1 ? year - 1 : year;
      const pm = month === 1 ? 12 : month - 1;
      return `${py}-${String(pm).padStart(2, '0')}`;
    });
    setSelectedDate(null);
  }, []);

  const nextMonth = useCallback(() => {
    setMonth((m) => {
      const { year, month } = parseYmd(m + '-01');
      const ny = month === 12 ? year + 1 : year;
      const nm = month === 12 ? 1 : month + 1;
      return `${ny}-${String(nm).padStart(2, '0')}`;
    });
    setSelectedDate(null);
  }, []);

  return {
    isMobile, companyCode,
    // 달력
    month, setMonth, prevMonth, nextMonth,
    calendarData, calLoading,
    selectedDate, setSelectedDate,
    // 목록
    listData, listLoading, taxTypeEnabled,
    // 은행코드 (활성, 드롭다운용)
    bankCodes,
    // 모달 핸들러 (계좌/세금정보 편집)
    ...modalHandlers,
    // 엑셀
    downloadExcel,
    downloadPrepayExcel,
    // 새로고침
    refresh: () => { fetchCalendar(); fetchList(); },
  };
}
